import { useEffect, useState } from "react";
import { useDataProvider } from "ra-core";
import { AlertTriangle, CheckCircle2, Clock3 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { CrmDataProvider } from "../providers/types";
import {
  displayEnvironment,
  type ReliabilityFinding,
  reliabilityFindings,
  type DurableEchoLedgerProjection,
  type DurableRecoveryAction,
} from "./durableEchoLedger";
import { formatDateTime } from "./sessionUtils";
import { DurableRecoveryActions } from "./DurableRecoveryActions";

type Props = { workloadId: string | null | undefined };

const findingLabels: Record<ReliabilityFinding, string> = {
  admitted_without_workflow: "Admitted turn has no workflow execution",
  workflow_without_heartbeat: "Workflow owner heartbeat is missing",
  generated_without_delivery_intent: "Generated asset has no delivery intent",
  delivery_without_provider_acceptance:
    "Delivery is active without provider acceptance",
  accepted_without_webhook: "Provider accepted the message; webhook is pending",
  outcome_unknown: "Provider outcome is unknown; reconciliation is required",
  terminal_before_retry_exhaustion:
    "Generation is terminal while a retryable attempt is active",
  projection_stale: "Projection is stale; refresh before recovery",
};

export function DurableEchoLedgerCard({ workloadId }: Props) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [projection, setProjection] =
    useState<DurableEchoLedgerProjection | null>(null);
  const [recoveryActions, setRecoveryActions] = useState<
    DurableRecoveryAction[]
  >([]);
  const [loading, setLoading] = useState(Boolean(workloadId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!workloadId) {
      setProjection(null);
      setRecoveryActions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    Promise.all([
      dataProvider.getDurableEchoLedger(workloadId),
      dataProvider.getDurableEchoRecoveryActions(workloadId),
    ])
      .then(([value, actions]) => {
        if (active) {
          setProjection(value);
          setRecoveryActions(actions);
        }
      })
      .catch(() => {
        if (active) setError("Durable ledger unavailable");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [dataProvider, workloadId]);

  if (!workloadId || (!loading && !projection && !error)) return null;
  const findings = projection
    ? reliabilityFindings(projection, new Date())
    : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          {findings.length > 0 ? (
            <AlertTriangle className="size-4 text-amber-600" />
          ) : (
            <CheckCircle2 className="size-4 text-emerald-600" />
          )}
          Durable execution ledger
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock3 className="size-4" /> Loading authoritative runtime state…
          </div>
        ) : error ? (
          <p className="text-muted-foreground">{error}</p>
        ) : projection ? (
          <>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">
                {displayEnvironment(projection.environment)}
              </Badge>
              <Badge variant="secondary">{projection.generationState}</Badge>
              {projection.deliveryState ? (
                <Badge variant="secondary">
                  delivery: {projection.deliveryState}
                </Badge>
              ) : null}
              <Badge variant={findings.length ? "destructive" : "outline"}>
                {findings.length
                  ? `${findings.length} finding(s)`
                  : "No reliability findings"}
              </Badge>
            </div>
            <dl className="grid gap-2 sm:grid-cols-3">
              <div>
                <dt className="text-muted-foreground">Version</dt>
                <dd>{projection.aggregateVersion}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Attempts</dt>
                <dd>{projection.attemptCount}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Config release</dt>
                <dd
                  className="truncate"
                  title={projection.frozenConfigReleaseId ?? undefined}
                >
                  {projection.frozenConfigReleaseId ?? "unknown"}
                </dd>
              </div>
            </dl>
            <dl className="grid gap-2 text-xs sm:grid-cols-2">
              <LedgerValue
                label="Workflow execution"
                value={projection.workflowExecutionId}
              />
              <LedgerValue
                label="Owner heartbeat"
                value={formatDateTime(projection.ownerHeartbeatAt)}
              />
              <LedgerValue
                label="Delivery heartbeat"
                value={formatDateTime(projection.deliveryHeartbeatAt)}
              />
              <LedgerValue
                label="Delivery lease"
                value={formatDateTime(projection.leaseExpiresAt)}
              />
              <LedgerValue
                label="Artifact receipt"
                value={projection.artifactReceiptDigest}
                mono
              />
              <LedgerValue
                label="Provider"
                value={
                  projection.providerMessageId
                    ? `${projection.providerStatus ?? "unknown"} / ${projection.providerMessageId}`
                    : projection.providerStatus
                }
                mono={Boolean(projection.providerMessageId)}
              />
              <LedgerValue
                label="Notification / refund"
                value={`${projection.notificationState ?? "unknown"} / ${projection.refundState ?? "unknown"}`}
              />
            </dl>
            {findings.length ? (
              <ul className="list-disc space-y-1 pl-5 text-amber-700">
                {findings.map((finding) => (
                  <li key={finding}>{findingLabels[finding]}</li>
                ))}
              </ul>
            ) : null}
            <DurableRecoveryActions
              workloadId={projection.workloadId}
              projection={projection}
              actions={recoveryActions}
              onSubmitted={(action) =>
                setRecoveryActions((current) => [action, ...current])
              }
            />
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function LedgerValue({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd
        className={mono ? "truncate font-mono" : "truncate"}
        title={value ?? undefined}
      >
        {value || "Unknown"}
      </dd>
    </div>
  );
}
